/**
 * Shared drill-down modal used by GTM Sales / GTM Sales Leader Insights.
 * Fetches GET /api/role-insights/drilldown?card=<key>&scope=self|team and
 * renders the resulting rows in a generic, columns-driven table.
 *
 * The modal stays UI-agnostic: the backend dictates column shape, so any
 * new card can be drilled into without touching this component.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, ChevronRight } from 'lucide-react';

const DrilldownModal = ({ title, card, scope = 'self', leadOwners = null, rangeParams = null, onClose, rowAction = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const qs = new URLSearchParams({ card, scope });
      if (leadOwners && leadOwners.length) qs.set('lead_owners', leadOwners.join(','));
      // Item 1 — forward the active date range so popup rows match the card count.
      if (rangeParams) Object.entries(rangeParams).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, v); });
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/drilldown?${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load detail');
    } finally {
      setLoading(false);
    }
  }, [card, scope, leadOwners, rangeParams]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const rows = data?.rows || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="insights-drilldown-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-800 text-base">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="insights-drilldown-close" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : error ? (
            <div className="m-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-12">No data for this card with the current filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid="insights-drilldown-table">
              <thead className="bg-gray-50 sticky top-0 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{c.label}</th>
                  ))}
                  {rowAction && <th className="px-4 py-2.5 text-right font-semibold">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={`${row.lead_id || i}-${i}`} className="hover:bg-gray-50" data-testid={`insights-drilldown-row-${i}`}>
                    {columns.map(c => {
                      // Phase 7 — colourise the "Status" escalation column.
                      if (c.key === 'status') {
                        const v = String(row[c.key] ?? '').trim();
                        const palette = {
                          Late:     'bg-amber-100 text-amber-700 border-amber-200',
                          Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
                          Critical: 'bg-rose-100 text-rose-700 border-rose-200',
                        };
                        return (
                          <td key={c.key} className="px-4 py-2.5 whitespace-nowrap" data-testid={`insights-drilldown-row-${i}-status`}>
                            {v ? (
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${palette[v] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                {v}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                        );
                      }
                      // Phase 9 — INR-format the financial drilldown amount columns.
                      if (['token_advance', 'finalize_setup_token', 'pending_payment'].includes(c.key)) {
                        const num = Number(row[c.key] ?? 0);
                        let formatted = '—';
                        try {
                          formatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num || 0);
                        } catch { formatted = `₹${num}`; }
                        return (
                          <td key={c.key} className="px-4 py-2.5 text-gray-800 tabular-nums whitespace-nowrap" data-testid={`insights-drilldown-row-${i}-${c.key}`}>
                            {formatted}
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className="px-4 py-2.5 text-gray-700 whitespace-nowrap max-w-xs truncate" title={String(row[c.key] ?? '')}>
                          {row[c.key] ?? '—'}
                        </td>
                      );
                    })}
                    {rowAction && (
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => rowAction.onClick(row)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-teal-600 text-white rounded-md hover:bg-teal-700"
                          data-testid={`insights-drilldown-row-action-${i}`}
                        >
                          {rowAction.label || 'Open'} <ChevronRight size={11} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrilldownModal;

/** Generic clickable KPI card used by GTM Sales / Leader insights. Renders
 *  identical visuals to the read-only Card but adds keyboard / cursor
 *  affordances so the user knows it's interactive. */
export const ClickableCard = ({ label, value, suffix, Icon, accent, testId, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 shadow-sm hover:shadow-md hover:border-teal-200 transition w-full text-left focus:outline-none focus:ring-2 focus:ring-teal-400"
    data-testid={testId}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent.bg}`}>
      <Icon size={22} className={accent.fg} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 truncate">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>
        {value}{suffix && <span className="text-sm font-medium text-gray-500 ml-1">{suffix}</span>}
      </p>
    </div>
  </button>
);
